import React from 'react';
import { Button, Input, useToasts } from 'freemium-ui';
import Downshift from 'downshift';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import Icon from 'shared/components/Icon';
import { ChipStyleMixin } from 'shared/styles';
import {
  useGetEmailVerficationStatus,
  useGetVerifiedEmails,
  useVerifyEmail,
} from '../../hooks';

type EmailVerifyInputProps = {
  value: string;
  placeholder?: string;
  label?: string;
  errorText?: string;
  disabled?: boolean;
  isErrored?: boolean;
  isRequired?: boolean;
  onChange: (emailString: string) => void;
};

const isEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value?.trim());

const EmailVerifyInput = (props: EmailVerifyInputProps) => {
  const { t } = useTranslation();

  const {
    disabled,
    value,
    onChange,
    label,
    placeholder,
    errorText,
    isErrored,
    isRequired,
  } = props;
  const [inputEmail, setInputEmail] = React.useState<string>(value);
  const [emailToVerify, setEmailToVerify] = React.useState<string>('');
  const [resendNumber, setResendNumber] = React.useState<number>(0);
  const resendTimer = React.useRef<number>(0);
  const intervalRef = React.useRef<NodeJS.Timeout>();
  const [, setToast] = useToasts();

  const initiateTimer = (time: number) => {
    setResendNumber(time);
    resendTimer.current = time;
    intervalRef.current = setInterval(() => {
      if (resendTimer.current > 0) {
        setResendNumber(resendTimer.current - 1);
        resendTimer.current = resendTimer.current - 1;
      } else {
        clearTimeout(intervalRef.current);
        intervalRef.current = undefined;
      }
    }, 1000);
  };

  const {
    data: verifiedEmails,
    refetch: refetchVerifiedEmails,
    isFetching: isfetchingVerifiedEmails,
  } = useGetVerifiedEmails();

  const {
    data: emailVerificationStatus,
    refetch: refetchEmailVerificationStatus,
    isFetching: isfetchingVerificationStatus,
  } = useGetEmailVerficationStatus({
    email: value,
    enabled: isEmail(emailToVerify),
  });
  const { mutate: verifyEmail, isLoading: isVerifyEmailLoading } =
    useVerifyEmail({
      setToast,
      callback: (isSuccess, _, payload) => {
        if (isSuccess && payload.sender_email) {
          setEmailToVerify(payload.sender_email);
          initiateTimer(10);
        }
      },
    });

  const verifiedEmailsToShow = React.useMemo(() => {
    return verifiedEmails?.filter((ve) =>
      ve.toLowerCase().includes(inputEmail.toLowerCase()),
    );
  }, [verifiedEmails, inputEmail]);

  const getEndAdornment = (inputValue?: string | null) => {
    if (!inputValue) return null;
    if (
      inputValue &&
      (verifiedEmails?.includes(inputValue) ||
        (emailVerificationStatus?.email === inputValue &&
          emailVerificationStatus?.is_verified))
    )
      return (
        <StatusChip className="evi-st-verified">{t('verified')}</StatusChip>
      );
    if (emailToVerify && !verifiedEmails?.includes(emailToVerify))
      return <StatusChip className="evi-st-pending">{t('pending')}</StatusChip>;
    return (
      <StatusButton>
        <Button
          type="secondary"
          onClick={() => {
            verifyEmail({
              sender_email: inputEmail,
              sender_name: '',
              workflow_name: '',
            });
          }}
          disabled={disabled || isVerifyEmailLoading || !isEmail(inputEmail)}
        >
          {t('verify_email')}
        </Button>
      </StatusButton>
    );
  };

  return (
    <EmailVerifyInputContainer>
      <Downshift
        inputValue={inputEmail}
        onInputValueChange={(val: string, stateAndHelpers: any) => {
          if (disabled) return;
          if (
            stateAndHelpers.type === '__autocomplete_change_input__' ||
            stateAndHelpers.type === 11
          ) {
            setInputEmail(val);
          }
        }}
        onSelect={(val) => {
          setInputEmail(val);
          onChange(val);
        }}
        itemToString={(item) => item?.name || ''}
      >
        {({
          getInputProps,
          getItemProps,
          getMenuProps,
          isOpen,
          inputValue,
          highlightedIndex,
          getRootProps,
        }) => (
          <div>
            <div
              {...getRootProps(undefined, { suppressRefError: true })}
              role="textbox"
            >
              <Input
                {...getInputProps()}
                label={
                  <CustomLabel>
                    <div className="cl-label-wf">
                      {label}
                      {isRequired ? <span className="lab-req">*</span> : ''}
                    </div>
                    {emailToVerify && (
                      <Button
                        className="input-refresh-btn"
                        htmlType="button"
                        onClick={() => {
                          refetchVerifiedEmails();
                          refetchEmailVerificationStatus();
                        }}
                        disabled={
                          disabled ||
                          isfetchingVerifiedEmails ||
                          isfetchingVerificationStatus
                        }
                      >
                        <Icon
                          width="10"
                          height="10"
                          glyph="refresh"
                          fill="currentColor"
                        />
                        &nbsp;{t('refresh')}
                      </Button>
                    )}
                  </CustomLabel>
                }
                endAdornment={getEndAdornment(inputValue)}
                endLabel={
                  emailToVerify && !verifiedEmails?.includes(emailToVerify) ? (
                    <Button
                      type="secondary"
                      className="wf-btn-resend-veriy"
                      onClick={() => {
                        verifyEmail({
                          sender_email: inputEmail,
                          sender_name: '',
                          workflow_name: '',
                        });
                      }}
                      disabled={
                        disabled ||
                        isVerifyEmailLoading ||
                        !isEmail(inputEmail) ||
                        resendNumber > 0
                      }
                    >
                      {resendNumber > 0
                        ? `${t('workflow.resend', {
                            count: resendNumber,
                          })}`
                        : t('resend')}
                    </Button>
                  ) : null
                }
                placeholder={placeholder}
                onBlur={(e) => {
                  onChange(e.target.value);
                }}
                ref={null}
                errorText={errorText}
                isErrored={isErrored}
              />
            </div>
            <div style={{ position: 'relative' }}>
              {isOpen && (verifiedEmailsToShow || []).length > 0 && (
                <PopoverContainer>
                  <ul {...getMenuProps()}>
                    {(verifiedEmailsToShow || []).map((item: string, index) => (
                      <li
                        key={`${item}-${index}`}
                        {...getItemProps({
                          key: item,
                          index,
                          item,
                          className:
                            highlightedIndex === index
                              ? 'dla-l-l-ai-li-selected'
                              : '',
                        })}
                      >
                        {item}
                      </li>
                    ))}
                  </ul>
                </PopoverContainer>
              )}
            </div>
          </div>
        )}
      </Downshift>
    </EmailVerifyInputContainer>
  );
};

export default EmailVerifyInput;

const CustomLabel = styled.div`
  display: flex;
  align-items: center;
  div.cl-label-wf {
    width: 100%;
  }
  .lab-req {
    font-weight: 700;
    font-size: 14px;
    padding-left: 3px;
    color: #d72d30;
  }
`;

const StatusButton = styled.div`
  padding: 2px;
  button {
    height: unset;
    padding: 2px 12px;
    outline: none !important;
    box-shadow: none !important;
    border: ipx solid #cfd7df !important;
  }
`;

const StatusChip = styled.div`
  ${ChipStyleMixin}
  border-radius: 4px;
  margin: 4px;
  &.evi-st-pending {
    background: rgb(255, 236, 240);
    color: rgb(200, 33, 36);
    border: 1px solid rgb(255, 208, 214);
  }
  &.evi-st-verified {
    background: rgb(224, 245, 240);
    color: rgb(0, 121, 88);
    border: 1px solid rgb(180, 229, 217);
  }
`;

const EmailVerifyInputContainer = styled.div`
  div.ev-i-c-ds {
    position: relative;
  }
  .input-refresh-btn {
    color: #2c5cc5;
    background: transparent !important;
    border: none;
    text-align: right;
    height: unset;
    padding: 0 4px;
    width: fit-content;
    box-shadow: unset;
  }
  .wf-btn-resend-veriy {
    height: 32px;
    margin-left: 8px;
  }
`;

export const PopoverContainer = styled.div`
  position: absolute;
  z-index: 1;
  top: 2px;
  width: 100%;
  ul {
    list-style: none;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    border-radius: 6px;
    box-shadow: 0px 2px 3px rgba(0, 0, 0, 0.06),
      0px 4px 20px rgba(25, 50, 71, 0.16);
    padding: 8px;
    max-height: 40vh;
    overflow: auto;
  }
  li {
    white-space: pre-wrap;
    cursor: pointer;
    outline: none;
    min-width: 100px;
    color: #12344d;
    border-radius: 4px;
    padding: 8px;
    font-size: 14px;
  }
  li.dla-l-l-ai-li-selected {
    background-color: #e5ecff;
  }
`;
